import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { getPaymentStatus } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [orderId, setOrderId] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let active = true;
    const poll = async () => {
      try {
        const res = await getPaymentStatus(sessionId);
        if (!active) return;
        setOrderId(res.order_id);
        if (res.payment_status === "paid") {
          setState("paid");
          clear();
          return;
        }
        if (attempts >= 6) {
          setState("pending");
          return;
        }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch {
        if (active) setState("error");
      }
    };
    poll();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center sm:px-6 lg:px-8">
      <Seo title="Payment | CA HVAC Direct" description="Payment status" />
      {state === "checking" && (<><Loader2 className="mx-auto h-12 w-12 animate-spin text-sky-600" /><h1 className="mt-5 font-heading text-2xl font-black text-slate-900">Confirming your payment…</h1></>)}
      {state === "paid" && (<>
        <CheckCircle2 className="mx-auto h-14 w-14 text-sky-600" />
        <h1 className="mt-5 font-heading text-3xl font-black tracking-tight text-slate-900">Payment Successful</h1>
        <p className="mt-3 text-slate-600">Thank you — your payment was received{orderId ? "" : ""}. We'll confirm your order and fulfillment details shortly.</p>
      </>)}
      {state === "pending" && (<>
        <Loader2 className="mx-auto h-12 w-12 text-amber-500" />
        <h1 className="mt-5 font-heading text-2xl font-black text-slate-900">Payment Processing</h1>
        <p className="mt-3 text-slate-600">Your payment is still processing. You'll receive confirmation once it completes.</p>
      </>)}
      {state === "error" && (<>
        <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
        <h1 className="mt-5 font-heading text-2xl font-black text-slate-900">We couldn't confirm payment</h1>
        <p className="mt-3 text-slate-600">If you were charged, please contact us and we'll sort it out right away.</p>
      </>)}
      <div className="mt-8 flex justify-center gap-3">
        <Button asChild className="rounded-sm bg-sky-700 hover:bg-sky-800"><Link to="/shop">Continue Shopping</Link></Button>
        <Button asChild variant="outline" className="rounded-sm border-slate-300"><Link to="/contact">Contact Us</Link></Button>
      </div>
    </div>
  );
}
